'use client'

import { useState } from 'react'
import { motion, MotionConfig } from 'framer-motion'
import { useLocale, useTranslations } from 'next-intl'
import { Check, MapPin, Clock, Bike, X } from 'lucide-react'
import { etaMinutes } from '@/lib/delivery/formatDropoff'
import { fixedWait, waitText, withCourier, type WaitEstimate } from '@/lib/microsite/waitEstimate'
import { confirmHero } from '@/lib/microsite/confirmHero'
import { confirmProgress, type ProgressStep } from '@/lib/orderTracking/confirmProgress'
import type { StepKey } from '@/lib/orderTracking/steps'
import { useOrderProgress } from './useOrderProgress'
import { formatPhone } from '@/lib/utils/phone'
import { WalletSaveButton } from './WalletSaveButton'
import { AccountClaimCard } from './AccountClaimCard'
import { TrackingCta } from './TrackingCta'
import { WelcomeCouponCard } from './WelcomeCouponCard'
import { SubstitutionPrefSelector } from './SubstitutionPrefSelector'
import { updateSubstitutionPref } from '@/app/actions/substitutionPublicActions'
import type { WelcomeCoupon } from '@/lib/marketing/welcomeCouponWire'
import type { SubstitutionPref, SubstitutionWarning } from '@/lib/online-orders/substitutionPrefs'

type Props = {
  orderId: string
  orderNumber: string
  businessName: string
  total: number
  accentColor: string
  pickupWaitMinutes?: number
  /** Tiempo de espera con el que se pagó (PR K). Solo cambia el texto en vivo. */
  waitEstimate?: WaitEstimate
  mapsUrl?: string
  /**
   * Hora en que se creó el pedido (ISO), para la hora del día de la tarjeta
   * principal. Sin ella: la del snapshot del seguimiento, o la del dispositivo
   * al montar la confirmación.
   */
  placedAt?: string | null
  /** ISO del pickup agendado (Fase 2), o null para pedido ASAP. */
  /** Modo mesa (QR): label de la mesa — cambia el copy a "te lo llevamos a tu mesa". */
  tableLabel?: string
  /** Modo mesa (QR): el cliente eligió para llevar — copy de "recoge en el mostrador". */
  qrTakeout?: boolean
  /** Teléfono que el cliente dejó en el pedido de mesa, o null si no dejó. */
  notifyPhone?: string | null
  scheduledFor?: string | null
  /** Zona horaria del local para formatear la hora agendada. */
  timezone?: string | null
  /**
   * Delivery: dirección de entrega ya formateada (con apto). Presente ⇒ el destino
   * es la casa del cliente, no el mostrador ni la mesa.
   */
  deliveryAddress?: string | null
  /** Delivery: ETA del courier en segundos (del quote), o null si el proveedor no la dio. */
  deliveryEtaSeconds?: number | null
  /** Token del intent para la captura opcional post-pago. null ⇒ no se ofrece. */
  receiptToken?: string | null
  /**
   * Link firmado para guardar la tarjeta Redy en el Wallet, o null.
   * Lo decide el SERVIDOR que creó la orden (plataforma incluida, con el
   * user-agent del request): aquí no se calcula nada, solo se pinta o no.
   */
  walletSaveUrl?: string | null
  /** Link firmado para guardar el pedido en la cuenta Redy, o null. Lo acuña el servidor que creó la orden. */
  accountClaimUrl?: string | null
  /** Link al seguimiento en vivo (/p/<código>), o null. Lo acuña el servidor que creó la orden. */
  trackingUrl?: string | null
  /** Cupón de bienvenida por aceptar SMS de promociones, o null. */
  welcomeCoupon?: WelcomeCoupon | null
  /** El cliente aceptó con oferta: si el cupón no vino, la tarjeta vuelve a preguntar. */
  expectWelcomeCoupon?: boolean
  // ─── Sustituciones (spec sustituciones §4.2, §4.4) ────────────────────────
  /** REQUERIDO a propósito: ningún caller futuro debe olvidar decidir el flag. */
  substitutionsEnabled: boolean
  /** Preferencia de la ORDEN entera que el cliente eligió en el pago (o que devolvió el servidor). */
  substitutionPref: SubstitutionPref
  /** Avisos de degradación (`contact` sin SMS, candidato inválido, etc.), de la respuesta de `/api/online-orders` o `intent-status`. */
  substitutionWarnings: SubstitutionWarning[]
  /** Código corto para que el cliente rastree/edite su sustitución después del
   *  pago. `null` si el servidor no pudo acuñarlo. */
  receiptCode: string | null
  /** Nombre de cada línea del carrito por índice, para pintar el aviso de una
   *  línea concreta (`cart_index >= 0`) — el carrito ya está vacío acá. */
  confirmedCartNames?: string[]
  /** El cliente dejó un teléfono usable y no rechazó SMS. */
  contactAllowed: boolean
  onClose: () => void
}

/**
 * Qué copy va bajo el "¡Pedido en camino!" en modo mesa (QR).
 *
 * Las DOS ramas miran el teléfono, no solo la de mesa: un pedido para llevar
 * sin número no puede prometer un SMS que nunca va a salir — no tenemos a
 * dónde mandarlo. Y el de mesa con número dice la regla real (te lo llevamos,
 * y el SMS es el respaldo si no te encontramos), la misma que el cliente ya
 * leyó en el carrito.
 */
function mesaConfirmKey(
  qrTakeout: boolean,
  hasPhone: boolean,
): 'confirmSmsPickup' | 'confirmPickupNoSms' | 'confirmToTableSms' | 'confirmToTable' {
  if (qrTakeout) return hasPhone ? 'confirmSmsPickup' : 'confirmPickupNoSms'
  return hasPhone ? 'confirmToTableSms' : 'confirmToTable'
}

export function ConfirmStep({
  orderId,
  orderNumber,
  businessName,
  total,
  accentColor,
  pickupWaitMinutes = 20,
  waitEstimate,
  mapsUrl,
  placedAt = null,
  tableLabel,
  qrTakeout = false,
  notifyPhone = null,
  scheduledFor = null,
  timezone,
  deliveryAddress = null,
  deliveryEtaSeconds = null,
  receiptToken = null,
  walletSaveUrl = null,
  accountClaimUrl = null,
  trackingUrl = null,
  welcomeCoupon = null,
  expectWelcomeCoupon = false,
  substitutionsEnabled,
  substitutionPref,
  substitutionWarnings,
  receiptCode,
  confirmedCartNames = [],
  contactAllowed,
  onClose,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tSub = useTranslations('public.onlineOrder.checkout.substitution')
  const mesaMode = !!tableLabel
  // Delivery excluye mesa (un cliente sentado no pide a domicilio), pero el orden
  // de las ramas lo deja explícito: mesa manda, luego domicilio, luego pickup.
  const deliveryMode = !mesaMode && !!deliveryAddress
  const googleMapsUrl = mapsUrl ?? `https://maps.google.com/maps?q=${encodeURIComponent(businessName)}`
  // Espera total estimada = preparación del local + viaje del courier. Se suman
  // porque es lo que el cliente realmente espera; si Uber no dio ETA, queda solo
  // la preparación (mismo número que en pickup). Es una estimación, va con "~".
  const etaCourierMin = etaMinutes(deliveryEtaSeconds)
  const deliveryTotalMin = pickupWaitMinutes + (etaCourierMin ?? 0)
  // En vivo: rango (cocina, + repartidor a domicilio). Fijo: el "~N minutos" de siempre.
  const liveWait = waitEstimate?.mode === 'live' ? waitEstimate : null
  const liveWaitLabel = liveWait
    ? waitText(deliveryMode ? withCourier(liveWait, etaCourierMin) : liveWait, 'approx', t)
    : null

  const fixedWaitLabel = t('minutesApprox', { minutes: deliveryMode ? deliveryTotalMin : pickupWaitMinutes })

  // ─── Tarjeta principal (confirmación v2): hora del día + avance en vivo ───
  // Mesa (QR) no la lleva: ahí la comida la trae el mesero o se recoge en el
  // mostrador y nunca hubo hora del día ni seguimiento — queda como hoy.
  const locale = useLocale()
  const snapshot = useOrderProgress(mesaMode ? null : receiptCode)
  const [mountedAt] = useState(() => Date.now())
  const receivedAt = snapshot?.steps?.receivedAt ? Date.parse(snapshot.steps.receivedAt) : NaN
  const placedAtMs = placedAt ? Date.parse(placedAt) : Number.isFinite(receivedAt) ? receivedAt : mountedAt
  const hero = mesaMode
    ? null
    : confirmHero({
        estimate: waitEstimate ?? fixedWait(pickupWaitMinutes),
        placedAtMs,
        courierMinutes: etaCourierMin,
        delivery: deliveryMode,
        scheduledFor,
        timeZone: timezone,
        locale,
      })
  const progress = confirmProgress(snapshot, deliveryMode)

  const [contact, setContact] = useState('')
  const [receiptState, setReceiptState] = useState<'idle' | 'sending' | 'sent' | 'saved' | 'error'>('idle')

  // ─── Sustituciones (spec sustituciones §4.4): editar la preferencia acá,
  // DESPUÉS del pago, con el código de recibo como capacidad. `pref` es la
  // ÚLTIMA guardada con éxito — si el servidor rechaza el cambio (locked,
  // not_editable, invalid, retry) el selector controlado por `pref` vuelve
  // solo a su valor anterior: no hace falta "revertir" nada a mano.
  const [pref, setPref] = useState<SubstitutionPref>(substitutionPref)
  const [editingPref, setEditingPref] = useState(false)
  const [savingPref, setSavingPref] = useState(false)
  const [prefStatus, setPrefStatus] = useState<'idle' | 'saved' | 'locked' | 'not_editable'>('idle')

  async function handlePrefChange(v: SubstitutionPref) {
    if (!receiptCode || savingPref) return
    setSavingPref(true)
    try {
      const r = await updateSubstitutionPref(receiptCode, v)
      if (r.ok) {
        setPref(v)
        setPrefStatus('saved')
        setEditingPref(false)
      } else if (r.error === 'locked') {
        setPrefStatus('locked')
        setEditingPref(false)
      } else if (r.error === 'not_editable') {
        // G3 (revisión final): "ya no admite cambios" no es lo mismo que "hay
        // una propuesta en curso" — no mentirle al cliente con el texto de locked.
        setPrefStatus('not_editable')
        setEditingPref(false)
      } else {
        // invalid / retry: el selector ya sigue mostrando `pref` (no se tocó);
        // se registra para diagnóstico y no se molesta al cliente con otro error.
        console.error('updateSubstitutionPref failed:', r.error)
      }
    } finally {
      setSavingPref(false)
    }
  }

  async function sendReceipt() {
    const value = contact.trim()
    if (!value || !receiptToken) return
    setReceiptState('sending')
    try {
      const res = await fetch('/api/online-orders/receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: receiptToken,
          method: value.includes('@') ? 'email' : 'sms',
          contact: value,
        }),
      })
      const data = (await res.json()) as { ok?: boolean; sent?: boolean }
      if (!res.ok || !data.ok) { setReceiptState('error'); return }
      setReceiptState(data.sent ? 'sent' : 'saved')
    } catch {
      setReceiptState('error')
    }
  }

  const titleLine = mesaMode || deliveryMode
    ? t('orderOnWay', { number: orderNumber })
    : t('orderConfirmed', { number: orderNumber })
  const subLine = mesaMode
    ? t(mesaConfirmKey(qrTakeout, !!notifyPhone), {
        table: tableLabel ?? '',
        phone: notifyPhone ? formatPhone(notifyPhone) : '',
      })
    : deliveryMode
      ? t('confirmDelivery')
      : t('confirmSmsReady')

  return (
    // reducedMotion="user": con prefers-reduced-motion las entradas no se mueven.
    <MotionConfig reducedMotion="user">
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-6 pb-8 flex flex-col items-center text-center">
        {hero ? (
          <>
            {/* 1 · Arriba: la hora del día y el avance. "¡Orden #042 confirmada!"
                baja a una línea chica — lo que el cliente quiere saber es CUÁNDO. */}
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-900"
            >
              <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
              </span>
              {titleLine}
            </motion.p>

            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              aria-label={t('confirmHeroAria')}
              className="relative mt-4 w-full shrink-0 overflow-hidden rounded-3xl border border-gray-100 bg-white px-5 pt-6 pb-5 shadow-sm"
            >
              <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: accentColor }} />
              <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
                {hero.kind === 'scheduled'
                  ? t('confirmHeroScheduled')
                  : deliveryMode
                    ? t('deliveryEtaLabel')
                    : t('confirmHeroPickup')}
              </p>
              <p
                className="mt-2 text-[clamp(26px,8.4vw,36px)] leading-none font-extrabold text-gray-900 tabular-nums whitespace-nowrap"
                style={{ letterSpacing: '-0.02em' }}
              >
                {hero.text}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                {hero.kind === 'scheduled' ? (
                  <span className="inline-block first-letter:uppercase">{hero.day}</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    {liveWaitLabel ?? fixedWaitLabel}
                  </span>
                )}
              </p>
              <ProgressSteps steps={progress} accentColor={accentColor} label={(k) => t(PROGRESS_KEY[k])} />
            </motion.section>

            <p className="mt-3 text-xs text-gray-500">{subLine}</p>
          </>
        ) : (
          <>
            {/* Mesa (QR): como siempre — sin hora del día ni avance. */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 12, stiffness: 200 }}
              className="mt-2 w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30"
            >
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </motion.div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">{titleLine}</h2>
            <p className="mt-1 text-sm text-gray-500">{subLine}</p>
            <div className="mt-6 w-full flex items-center gap-3 rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-gray-500" />
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500">{t('estimatedTimeLabel')}</p>
                <p className="text-sm font-semibold text-gray-900">{fixedWaitLabel}</p>
              </div>
            </div>
          </>
        )}

        {/* 2 · El pase, justo debajo de la hora y el avance: es la acción de más
            valor (abre la relación con el cliente y es lo que hace que los avisos
            existan) y aquí cae donde la mirada ya está, sin hacer scroll. Carbón,
            no el acento del local: el pase es de Redy. `walletSaveUrl` llega null
            si la plataforma no lo soporta o falta configuración: no se pinta. */}
        {(walletSaveUrl || trackingUrl || accountClaimUrl) && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-5 w-full"
        >
          <WalletSaveButton url={walletSaveUrl} />
          {/* 3 · Seguir en Redy to Eat: enlace secundario al portal. El tracker ya
              trae "Guardar en tu cuenta"; la tarjeta de la cuenta queda de
              RESPALDO para cuando no hubo código de recibo. */}
          <div className="mt-3">
            {trackingUrl ? <TrackingCta url={trackingUrl} /> : <AccountClaimCard url={accountClaimUrl} />}
          </div>
        </motion.div>
        )}

        {/* 4 · Al final, los detalles. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 w-full"
        >
          <div className="flex items-center gap-3 mb-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t('confirmDetailsLabel')}</p>
            <span className="flex-1 h-px bg-gray-100" />
          </div>

          <div className="space-y-3">
            {/* Destino: mesa (QR dine-in) · mostrador (QR takeout) · domicilio · pickup con mapa */}
            {mesaMode ? (
              <div className="flex items-center gap-3 rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accentColor}18` }}
                >
                  <MapPin className="w-4 h-4" style={{ color: accentColor }} />
                </div>
                <div className="text-left">
                  <p className="text-xs text-gray-500">{qrTakeout ? t('pickupAtCounter') : t('weBringToYou')}</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {qrTakeout ? t('theCounter', { business: businessName }) : t('table', { table: tableLabel ?? '' })}
                  </p>
                </div>
              </div>
            ) : deliveryMode ? (
              <div className="flex items-start gap-3 rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accentColor}18` }}
                >
                  <Bike className="w-4 h-4" style={{ color: accentColor }} />
                </div>
                <div className="text-left min-w-0">
                  <p className="text-xs text-gray-500">{t('deliveryToLabel')}</p>
                  <p className="text-sm font-semibold text-gray-900 break-words">{deliveryAddress}</p>
                  {etaCourierMin != null && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('deliveryTripMinutes', { minutes: etaCourierMin })}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3 hover:bg-gray-100 transition-colors"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accentColor}18` }}
                >
                  <MapPin className="w-4 h-4" style={{ color: accentColor }} />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="text-xs text-gray-500">{t('pickupAtLabel')}</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{businessName}</p>
                </div>
                <span className="text-xs font-medium flex-shrink-0" style={{ color: accentColor }}>
                  {t('viewMap')} →
                </span>
              </a>
            )}

            {/* Número de orden y total */}
            <div className="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3 text-left">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">{t('confirmOrderNumberLabel')}</p>
                <p className="text-sm font-semibold text-gray-900 tabular-nums">#{orderNumber}</p>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">{t('totalPaid')}</p>
                <p className="text-lg font-bold text-gray-900 tabular-nums">${total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <WelcomeCouponCard
          initial={welcomeCoupon}
          expect={expectWelcomeCoupon}
          receiptToken={receiptToken}
          accentColor={accentColor}
        />

        {substitutionsEnabled && receiptCode && substitutionPref && (
          <div className="mt-3 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4 text-left">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-900">
                {t('substitution.confirmRow', { pref: tSub(pref) })}
              </p>
              {prefStatus !== 'locked' && prefStatus !== 'not_editable' && !editingPref && (
                <button
                  type="button"
                  onClick={() => setEditingPref(true)}
                  className="flex-shrink-0 text-sm font-semibold underline-offset-2 hover:underline"
                  style={{ color: accentColor }}
                >
                  {t('substitution.change')}
                </button>
              )}
            </div>
            {prefStatus === 'saved' && !editingPref && (
              <p className="mt-1 text-xs font-medium" style={{ color: accentColor }}>{t('substitution.saved')}</p>
            )}
            {prefStatus === 'locked' && (
              <p className="mt-1 text-xs text-red-600">{t('substitution.locked')}</p>
            )}
            {prefStatus === 'not_editable' && (
              <p className="mt-1 text-xs text-red-600">{t('substitution.notEditable')}</p>
            )}
            {editingPref && (
              <div className="mt-3">
                <SubstitutionPrefSelector
                  value={pref}
                  onChange={handlePrefChange}
                  contactAllowed={contactAllowed}
                  accentColor={accentColor}
                  compact
                />
              </div>
            )}
            {substitutionWarnings.map((w, i) => (
              <p key={i} className="mt-2 text-xs text-gray-500">
                {w.reason === 'pref_not_saved'
                  ? t('substitution.warningNotSaved')
                  : w.cart_index === -1
                    ? t('substitution.warningContact')
                    : t('substitution.warningLine', { name: confirmedCartNames[w.cart_index] ?? '' })}
              </p>
            ))}
          </div>
        )}

        {receiptToken && receiptState !== 'sent' && receiptState !== 'saved' && (
          <div className="mt-3 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4 text-left">
            <p className="text-sm font-semibold text-gray-900">{t('receiptAskTitle')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('receiptAskHelp')}</p>
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={t('receiptAskPlaceholder')}
                className="flex-1 min-w-0 rounded-xl border border-gray-200 px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              />
              <button
                type="button"
                onClick={sendReceipt}
                disabled={receiptState === 'sending' || contact.trim().length === 0}
                className="flex-shrink-0 rounded-xl px-4 py-2.5 text-white text-sm font-bold disabled:opacity-40"
                style={{ backgroundColor: accentColor }}
              >
                {t('receiptAskSend')}
              </button>
            </div>
            {receiptState === 'error' && (
              <p className="mt-2 text-xs text-red-600">{t('receiptAskError')}</p>
            )}
          </div>
        )}

        {(receiptState === 'sent' || receiptState === 'saved') && (
          <p className="mt-4 text-sm font-medium" style={{ color: accentColor }}>
            {receiptState === 'sent' ? t('receiptAskSent') : t('receiptAskSaved')}
          </p>
        )}

        <a
          href={`/receipt/${orderId}`}
          target="_blank"
          rel="noreferrer"
          className="mt-4 text-sm font-semibold underline-offset-2 hover:underline"
          style={{ color: accentColor }}
        >
          {t('viewReceipt')} →
        </a>

      </div>

      <div
        className="border-t border-gray-100 bg-white px-5 pt-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 active:scale-[0.99]"
          style={{ backgroundColor: accentColor }}
        >
          {t('done')}
        </button>
      </div>
    </div>
    </MotionConfig>
  )
}

const PROGRESS_KEY: Record<StepKey, string> = {
  recibido: 'confirmStepReceived',
  en_cocina: 'confirmStepKitchen',
  listo: 'confirmStepReady',
  en_camino: 'confirmStepOnTheWay',
  entregado: 'confirmStepDelivered',
  cancelado: 'confirmStepCancelled',
}

/**
 * Avance Recibida → En cocina → Lista (→ En camino → Entregada). Lo mueve el
 * sondeo de `useOrderProgress`; el paso actual late solo con movimiento
 * permitido (`motion-safe:`). Lista ordenada para los lectores de pantalla, con
 * el paso actual marcado con aria-current.
 */
function ProgressSteps({
  steps,
  accentColor,
  label,
}: {
  steps: ProgressStep[]
  accentColor: string
  label: (k: StepKey) => string
}) {
  return (
    <ol className="mt-5 flex items-start" data-testid="confirm-progress">
      {steps.map((s, i) => {
        const cancelled = s.key === 'cancelado'
        const done = s.status === 'done'
        const current = s.status === 'current'
        const color = cancelled ? '#DC2626' : accentColor
        return (
          <li
            key={s.key}
            className="relative flex-1 flex flex-col items-center"
            aria-current={current ? 'step' : undefined}
          >
            {i > 0 && (
              <span
                aria-hidden="true"
                className="absolute top-[9px] h-[3px] rounded-full transition-colors duration-500"
                style={{ left: 'calc(-50% + 14px)', right: 'calc(50% + 14px)', backgroundColor: done || current ? color : '#E5E7EB' }}
              />
            )}
            <span
              aria-hidden="true"
              className="relative z-10 w-[21px] h-[21px] rounded-full flex items-center justify-center transition-colors duration-500"
              style={{
                backgroundColor: done || current ? color : '#FFFFFF',
                border: done || current ? 'none' : '2px solid #E5E7EB',
              }}
            >
              {done && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
              {current && cancelled && <X className="w-3 h-3 text-white" strokeWidth={3.5} />}
              {current && !cancelled && (
                <>
                  <span className="absolute inset-0 rounded-full motion-safe:animate-ping opacity-40" style={{ backgroundColor: color }} />
                  <span className="relative w-[7px] h-[7px] rounded-full bg-white" />
                </>
              )}
            </span>
            <span
              className={`mt-1.5 text-[11px] leading-tight ${current ? 'font-bold text-gray-900' : done ? 'font-medium text-gray-700' : 'text-gray-400'}`}
            >
              {label(s.key)}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
